import React from 'react';
import ReactDOM from 'react-dom';
import { act } from 'react-dom/test-utils';

// TumbNail は react-intersection-observer (IntersectionObserver) に依存しており、
// jsdom 環境でその挙動に依存すると不安定なため、このテストの対象外としてスタブ化する。
jest.mock('../event/thumb_nail', () => () => null);

// eslint-disable-next-line import/first
import SearchEventBoxVertical from './search_event_box_vertical2';

const baseEventItem = {
  id: 1,
  title: 'テストイベント',
  circle: { id: 10, name: 'テストサークル', is_company_owned: false },
  is_full: false,
  is_application_ended: false,
  event_month_only: 9,
  event_date_only: 20,
  event_date_weekday_kanji: '日',
  event_time_only: '19:00',
  is_online: false,
  countries_pref: null,
  place: null,
  valid_events_plans_users: [],
  valid_events_follows: [],
  max_point: 0,
  met_friend: null,
  ad_type: null,
  hashed_event_pr_allocation_id: null,
  hashed_circles_ad_id: null,
};

describe('SearchEventBoxVertical', () => {
  let containers;

  const renderCard = (props) => {
    const container = document.createElement('div');
    document.body.appendChild(container);
    containers.push(container);
    act(() => {
      ReactDOM.render(<SearchEventBoxVertical {...props} />, container);
    });
    return container;
  };

  beforeEach(() => {
    containers = [];
  });

  afterEach(() => {
    containers.forEach((container) => {
      ReactDOM.unmountComponentAtNode(container);
      container.remove();
    });
  });

  // お気に入り（フォロー）ボタンの表示切り替え
  it('isFollowed に応じて data-followed とハートアイコンの画像が切り替わる', () => {
    const followedContainer = renderCard({
      eventItem: baseEventItem,
      currentUser: { id: 1 },
      isFollowed: true,
    });
    const followedButton = followedContainer.querySelector('.btn-follow');
    expect(followedButton.getAttribute('data-followed')).toBe('true');
    expect(followedButton.querySelector('img').getAttribute('src')).toBe(
      '/assets2019/img/favorite_black_24dp.svg'
    );

    const unfollowedContainer = renderCard({
      eventItem: baseEventItem,
      currentUser: { id: 1 },
      isFollowed: false,
    });
    const unfollowedButton = unfollowedContainer.querySelector('.btn-follow');
    expect(unfollowedButton.getAttribute('data-followed')).toBe('false');
    expect(unfollowedButton.querySelector('img').getAttribute('src')).toBe(
      '/assets2019/img/favorite_border_black_24dp.svg'
    );
  });

  // 「会っています」ピル表示
  it('met_friend がある時だけ「〜さんと前に会っています」ピルを表示する', () => {
    const withMetFriendContainer = renderCard({
      eventItem: {
        ...baseEventItem,
        met_friend: { user_id: 1, name: 'テスト', avatar_thumb: 'x' },
      },
      currentUser: null,
    });
    expect(withMetFriendContainer.textContent).toContain(
      'テストさんと前に会っています'
    );

    const withoutMetFriendContainer = renderCard({
      eventItem: { ...baseEventItem, met_friend: null },
      currentUser: null,
    });
    expect(withoutMetFriendContainer.textContent).not.toContain(
      'さんと前に会っています'
    );
  });

  // 名前欄はCSSのtext-overflow:ellipsisではなくJS側で切り詰める。
  // 4文字以下はそのまま、5文字以上は先頭3文字+「…」にする。
  it('met_friend.name が4文字ちょうどならそのまま全文表示される', () => {
    const container = renderCard({
      eventItem: {
        ...baseEventItem,
        met_friend: { user_id: 1, name: 'なまえが', avatar_thumb: 'x' },
      },
      currentUser: null,
    });
    expect(container.textContent).toContain('なまえがさんと前に会っています');
  });

  it('met_friend.name が5文字以上の場合、先頭3文字+「…」に切り詰められる', () => {
    const container = renderCard({
      eventItem: {
        ...baseEventItem,
        met_friend: { user_id: 1, name: 'なまえがとても長いテストユーザー', avatar_thumb: 'x' },
      },
      currentUser: null,
    });
    expect(container.textContent).toContain('なまえ…さんと前に会っています');
    expect(container.textContent).not.toContain('なまえがとても長い');
  });

  // 主催者バッジが後続アバターに隠れないよう、主催者を先頭に並び替え、先頭ほど手前に来るzIndexを振る
  it('主催者が配列の途中にいても、アバター一覧の先頭に来て一番手前のzIndexになる', () => {
    const container = renderCard({
      eventItem: {
        ...baseEventItem,
        valid_events_plans_users: [
          { id: 1, participant_type: 2, user: { avatar_thumb: 'a.png' } },
          { id: 2, participant_type: 1, user: { avatar_thumb: 'b.png' } },
          { id: 3, participant_type: 2, user: { avatar_thumb: 'c.png' } },
        ],
      },
      currentUser: null,
    });

    const listItems = container.querySelectorAll('.event-avatar-list-fade-2026 li');
    expect(listItems.length).toBe(3);

    const firstItem = listItems[0];
    expect(firstItem.querySelector('.event-organizer-chip-2026').textContent).toBe('主催');

    const zIndexes = Array.from(listItems).map((li) => Number(li.style.zIndex));
    expect(zIndexes[0]).toBe(Math.max(...zIndexes));
  });

  // 満席・募集終了バッジの排他表示（is_full を優先する）
  it('is_full が true なら「満席」、is_full が false かつ is_application_ended が true なら「募集終了」を表示する', () => {
    const fullContainer = renderCard({
      eventItem: {
        ...baseEventItem,
        is_full: true,
        is_application_ended: true,
      },
      currentUser: null,
    });
    expect(fullContainer.textContent).toContain('満席');
    expect(fullContainer.textContent).not.toContain('募集終了');

    const endedContainer = renderCard({
      eventItem: {
        ...baseEventItem,
        is_full: false,
        is_application_ended: true,
      },
      currentUser: null,
    });
    expect(endedContainer.textContent).toContain('募集終了');
    expect(endedContainer.textContent).not.toContain('満席');
  });
});
